import type { IGraphClient } from '../../src/services/GraphService';

/** A file or folder as the new screens need it (Graph driveItem, trimmed). */
export interface IItem {
  id: string;
  name: string;
  isFolder: boolean;
  size?: number;
  webUrl?: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  parentId?: string;
  downloadUrl?: string;
  childCount?: number;
}

interface IGraphItem {
  id: string;
  name: string;
  size?: number;
  webUrl?: string;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
  parentReference?: { id?: string };
  folder?: { childCount?: number };
  file?: object;
  '@microsoft.graph.downloadUrl'?: string;
}

function toItem(g: IGraphItem): IItem {
  return {
    id: g.id, name: g.name, isFolder: !!g.folder, size: g.size, webUrl: g.webUrl,
    createdDateTime: g.createdDateTime, lastModifiedDateTime: g.lastModifiedDateTime,
    parentId: g.parentReference?.id, downloadUrl: g['@microsoft.graph.downloadUrl'], childCount: g.folder?.childCount
  };
}

export function extensionOf(name: string): string {
  const dot: number = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Children of a folder, every page. Files come with a short-lived download link. */
export async function listChildren(graph: IGraphClient, driveId: string, folderId: string, max: number = 5000): Promise<IItem[]> {
  const out: IItem[] = [];
  let url: string | undefined = `/drives/${driveId}/items/${folderId}/children?$top=200`;
  while (url && out.length < max) {
    const page: { value: IGraphItem[]; '@odata.nextLink'?: string } = await graph.api(url).get();
    out.push(...page.value.map(toItem));
    url = page['@odata.nextLink'];
  }
  return out;
}

export async function getRoot(graph: IGraphClient, driveId: string): Promise<IItem> {
  return toItem(await graph.api(`/drives/${driveId}/root`).get());
}

export async function getItem(graph: IGraphClient, driveId: string, itemId: string): Promise<IItem | undefined> {
  try {
    return toItem(await graph.api(`/drives/${driveId}/items/${itemId}`).get());
  } catch (error) {
    if ((error as { statusCode?: number }).statusCode === 404) {
      return undefined;
    }
    throw error;
  }
}

/** An item by its path from the top of the library ("Policies/HR"); undefined when it isn't there. */
export async function getItemByPath(graph: IGraphClient, driveId: string, path: string): Promise<IItem | undefined> {
  const encoded: string = path.split('/').map(encodeURIComponent).join('/');
  try {
    return toItem(await graph.api(`/drives/${driveId}/root:/${encoded}`).get());
  } catch (error) {
    if ((error as { statusCode?: number }).statusCode === 404) {
      return undefined;
    }
    throw error;
  }
}

/** The library's Inbox folder: found by id, then by name, else created at the top level. */
export async function ensureInbox(graph: IGraphClient, driveId: string, name: string, knownId?: string): Promise<IItem> {
  if (knownId) {
    const known: IItem | undefined = await getItem(graph, driveId, knownId);
    if (known && known.isFolder) {
      return known;
    }
  }
  const root: IItem = await getRoot(graph, driveId);
  const existing: IItem | undefined = (await listChildren(graph, driveId, root.id)).filter(i => i.isFolder && i.name.toLowerCase() === name.toLowerCase())[0];
  if (existing) {
    return existing;
  }
  return toItem(await graph.api(`/drives/${driveId}/items/${root.id}/children`).post({ name, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' }));
}

/** A picture of the first page, for the review screen (a short-lived link; undefined when SharePoint has none). */
export async function thumbnailUrl(graph: IGraphClient, driveId: string, itemId: string): Promise<string | undefined> {
  try {
    const r: { value: { large?: { url?: string } }[] } = await graph.api(`/drives/${driveId}/items/${itemId}/thumbnails`).get();
    return r.value[0]?.large?.url;
  } catch {
    return undefined;
  }
}

/** Files in the library whose name or text matches, as SharePoint search finds them. */
export async function searchFiles(graph: IGraphClient, driveId: string, query: string): Promise<IItem[]> {
  const q: string = query.replace(/'/g, "''").slice(0, 200);
  const r: { value: IGraphItem[] } = await graph.api(`/drives/${driveId}/root/search(q='${encodeURIComponent(q)}')?$top=25`).get();
  return r.value.filter(i => !i.folder).map(toItem);
}

/** Free space in the library's site, in bytes (undefined when SharePoint doesn't say). */
export async function freeSpace(graph: IGraphClient, driveId: string): Promise<number | undefined> {
  try {
    const d: { quota?: { remaining?: number } } = await graph.api(`/drives/${driveId}?$select=quota`).get();
    return d.quota?.remaining;
  } catch {
    return undefined;
  }
}

/** Creates a folder at the top of the library (fails if the name is taken). */
export async function createTopFolder(graph: IGraphClient, driveId: string, name: string): Promise<IItem> {
  const root: IItem = await getRoot(graph, driveId);
  return toItem(await graph.api(`/drives/${driveId}/items/${root.id}/children`).post({ name, folder: {}, '@microsoft.graph.conflictBehavior': 'rename' }));
}

/**
 * Creates a new folder at the top of the library under this name, or "name (2)", "name (3)"… when
 * the name is taken, so an existing folder is never reused or overwritten.
 */
export async function createNewTopFolder(graph: IGraphClient, driveId: string, name: string): Promise<IItem> {
  const root: IItem = await getRoot(graph, driveId);
  const taken: { [n: string]: boolean } = {};
  (await listChildren(graph, driveId, root.id)).forEach(c => { taken[c.name.toLowerCase()] = true; });
  for (let n = 1; n < 100; n++) {
    const candidate: string = n === 1 ? name : `${name} (${n})`;
    if (taken[candidate.toLowerCase()]) {
      continue;
    }
    try {
      return toItem(await graph.api(`/drives/${driveId}/items/${root.id}/children`).post({ name: candidate, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' }));
    } catch (error) {
      if ((error as { statusCode?: number }).statusCode !== 409) {
        throw error;
      }
      // Someone made it a moment ago: try the next name.
    }
  }
  throw new Error(`There are already too many folders called "${name}".`);
}

/** The Graph SDK request also uploads; the shared client type only lists what the engine needs. */
interface IUploadRequest {
  header(name: string, value: string): IUploadRequest;
  put(body: unknown): Promise<IGraphItem>;
}

/** Saves a small file (up to a few MB) into a folder; fails rather than replace a file of the same name. */
export async function uploadSmallFile(graph: IGraphClient, driveId: string, folderId: string, name: string, content: string, contentType: string): Promise<IItem> {
  const request = graph.api(`/drives/${driveId}/items/${folderId}:/${encodeURIComponent(name)}:/content?@microsoft.graph.conflictBehavior=fail`) as unknown as IUploadRequest;
  return toItem(await request.header('Content-Type', contentType).put(new Blob([content], { type: contentType })));
}

/**
 * Starts SharePoint copying a file or folder (with everything inside) into another folder. The
 * copy runs in SharePoint; watch it with copiedSize.
 */
export async function startCopy(graph: IGraphClient, driveId: string, itemId: string, intoFolderId: string, name?: string): Promise<void> {
  await graph.api(`/drives/${driveId}/items/${itemId}/copy?@microsoft.graph.conflictBehavior=rename`).post({ parentReference: { driveId, id: intoFolderId }, ...(name ? { name } : {}) });
}

/** Bytes copied so far into a folder, per child name. */
export async function copiedSizes(graph: IGraphClient, driveId: string, folderId: string): Promise<{ [name: string]: number }> {
  const out: { [name: string]: number } = {};
  for (const c of await listChildren(graph, driveId, folderId)) {
    out[c.name.toLowerCase()] = c.size ?? 0;
  }
  return out;
}

export function formatBytes(n: number): string {
  const units: string[] = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  let v: number = n;
  let i: number = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}
